import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Star, Shield, Truck, ShoppingCart, Quote, ChevronRight, 
  Hand, CheckCircle, Sparkles, IndianRupee, Heart, Flame,
  ArrowRight, Send, CreditCard, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  Shield, Truck, Hand, CheckCircle, Sparkles, IndianRupee, 
  Heart, Flame, CreditCard, RefreshCw, Star
};

const HomePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hero, setHero] = useState(null);
  const [trustBadges, setTrustBadges] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [festiveBanner, setFestiveBanner] = useState(null);
  const [whyChoose, setWhyChoose] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [newsletter, setNewsletter] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [cartItems, setCartItems] = useState([]);
  const [wishlist, setWishlist] = useState([]);

  useEffect(() => {
    fetchAllData();
    trackPageView();
    loadCart();
  }, []);

  const loadCart = () => {
    const saved = localStorage.getItem('indicraft_cart');
    if (saved) setCartItems(JSON.parse(saved));
  };

  const fetchAllData = async () => {
    try {
      const [heroRes, trustRes, catRes, prodRes, festiveRes, whyRes, testRes, newsRes] = await Promise.all([
        axios.get(`${API_URL}/api/cms/hero`),
        axios.get(`${API_URL}/api/cms/trust-badges`),
        axios.get(`${API_URL}/api/categories`),
        axios.get(`${API_URL}/api/products?bestseller=true`),
        axios.get(`${API_URL}/api/cms/festive-banner`),
        axios.get(`${API_URL}/api/cms/why-choose`),
        axios.get(`${API_URL}/api/testimonials?featured_only=true`),
        axios.get(`${API_URL}/api/cms/newsletter`),
      ]);

      setHero(heroRes.data);
      setTrustBadges(trustRes.data.badges || []);
      setCategories(catRes.data || []);
      setProducts(prodRes.data || []);
      setFestiveBanner(festiveRes.data);
      setWhyChoose(whyRes.data);
      setTestimonials(testRes.data || []);
      setNewsletter(newsRes.data);
    } catch (error) {
      console.error('Failed to fetch homepage data:', error);
    } finally {
      setLoading(false);
    }
  };

  const trackPageView = async () => {
    try {
      await axios.post(`${API_URL}/api/analytics/track`, {
        event_type: 'page_view',
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Analytics tracking failed:', error);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    
    try {
      await axios.post(`${API_URL}/api/newsletter/subscribe?email=${encodeURIComponent(newsletterEmail)}`);
      toast.success('Thank you for subscribing!');
      setNewsletterEmail('');
    } catch (error) {
      toast.error('Subscription failed. Please try again.');
    }
  };

  const addToCart = (product) => {
    const existing = cartItems.find(item => item.id === product.id);
    let newCart;
    if (existing) {
      newCart = cartItems.map(item => 
        item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
      );
    } else {
      newCart = [...cartItems, { ...product, quantity: 1 }];
    }
    setCartItems(newCart);
    localStorage.setItem('indicraft_cart', JSON.stringify(newCart));
    toast.success(`${product.name} added to cart!`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-cream">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-maroon/10 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-3xl text-maroon font-accent">ॐ</span>
          </div>
          <p className="text-maroon/60 font-medium">Loading divine collection...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Header cartCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)} wishlistCount={wishlist.length} />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section 
          className="relative min-h-[70vh] md:min-h-[80vh] flex items-center justify-center overflow-hidden"
          data-testid="hero-section"
        >
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(${hero?.background_image || 'https://images.unsplash.com/photo-1674314975271-a28bcfbfb0dd?crop=entropy&cs=srgb&fm=jpg&q=85'})` 
            }}
          >
            <div 
              className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent"
              style={{ opacity: hero?.overlay_opacity || 0.7 }}
            />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/20 backdrop-blur-sm mb-6">
                <Sparkles className="w-4 h-4 text-gold" />
                <span className="text-sm text-gold font-medium">Premium Puja Collection</span>
              </div>
              
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6"
                data-testid="hero-title"
              >
                {hero?.headline || 'Sacred Puja Essentials for Your Divine Home'}
              </h1>
              
              <p 
                className="text-lg md:text-xl text-white/80 mb-8 leading-relaxed"
                data-testid="hero-subtitle"
              >
                {hero?.subheadline || 'Authentic | Premium Quality | Delivered Across India'}
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => navigate(hero?.cta_primary_link || '/shop')}
                  className="bg-gold hover:bg-gold-dark text-maroon-dark font-semibold px-8 py-6 rounded-full text-lg shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                  data-testid="hero-cta-primary"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  {hero?.cta_primary_text || 'Shop Now'}
                </Button>
                
                {hero?.cta_secondary_text && (
                  <Button
                    onClick={() => navigate(hero?.cta_secondary_link || '/shop')}
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-maroon font-semibold px-8 py-6 rounded-full text-lg transition-all"
                    data-testid="hero-cta-secondary"
                  >
                    {hero?.cta_secondary_text}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-cream to-transparent" />
        </section>

        {/* Trust Badges Section */}
        {trustBadges.length > 0 && (
          <section className="py-8 bg-white border-y border-gold/10" data-testid="trust-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-wrap justify-center gap-8 md:gap-12">
                {trustBadges.filter(b => b.is_active).map((badge) => {
                  const IconComponent = iconMap[badge.icon] || Shield;
                  return (
                    <div 
                      key={badge.id} 
                      className="flex items-center gap-3 text-maroon"
                      data-testid={`trust-badge-${badge.id}`}
                    >
                      <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-gold" />
                      </div>
                      <span className="font-medium text-sm md:text-base">{badge.title}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Featured Categories */}
        {categories.length > 0 && (
          <section className="py-16 md:py-20" data-testid="categories-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <span className="text-gold font-accent text-sm tracking-widest">EXPLORE</span>
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-maroon mt-2">
                  Featured Categories
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-transparent via-gold to-transparent mx-auto mt-4" />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
                {categories.slice(0, 6).map((category) => (
                  <button
                    key={category.id}
                    onClick={() => navigate(`/shop?category=${category.slug}`)}
                    className="group relative aspect-square rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                    data-testid={`category-${category.id}`}
                  >
                    <img
                      src={category.image_url || 'https://via.placeholder.com/400'}
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-maroon/90 via-maroon/40 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-white font-heading font-semibold text-sm md:text-base text-center">
                        {category.name}
                      </h3>
                    </div>
                    <div className="absolute inset-0 border-2 border-gold/0 group-hover:border-gold/50 rounded-2xl transition-colors duration-300" />
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Best Selling Products */}
        {products.length > 0 && (
          <section className="py-16 md:py-20 bg-white" data-testid="bestsellers-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
                <div>
                  <span className="text-gold font-accent text-sm tracking-widest">POPULAR</span>
                  <h2 className="text-3xl md:text-4xl font-heading font-bold text-maroon mt-2">
                    Best Selling Products
                  </h2>
                  <div className="w-24 h-1 bg-gradient-to-r from-gold to-transparent mt-4" />
                </div>
                <Button
                  onClick={() => navigate('/shop')}
                  variant="ghost"
                  className="text-maroon hover:text-maroon-dark mt-4 md:mt-0"
                >
                  View All Products
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {products.slice(0, 8).map((product) => (
                  <div
                    key={product.id}
                    className="group bg-cream rounded-2xl overflow-hidden border border-transparent hover:border-gold/30 shadow-sm hover:shadow-[0_8px_30px_rgba(212,175,55,0.15)] transition-all duration-500"
                    data-testid={`product-card-${product.id}`}
                  >
                    <div className="relative aspect-square overflow-hidden">
                      <img
                        src={product.images?.[0] || 'https://via.placeholder.com/400'}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 cursor-pointer"
                        onClick={() => navigate(`/product/${product.id}`)}
                      />
                      
                      {product.discount_percentage > 0 && (
                        <div className="absolute top-3 left-3">
                          <span className="bg-saffron text-white text-xs font-bold px-2 py-1 rounded-full">
                            {product.discount_percentage}% OFF
                          </span>
                        </div>
                      )}
                      
                      {product.is_new && (
                        <div className="absolute top-3 right-3">
                          <span className="bg-maroon text-white text-xs font-bold px-2 py-1 rounded-full">
                            NEW
                          </span>
                        </div>
                      )}
                      
                      {product.stock < 10 && product.stock > 0 && (
                        <div className="absolute bottom-3 left-3 right-3">
                          <span className="bg-red-500/90 text-white text-xs font-medium px-2 py-1 rounded-full">
                            Only {product.stock} left!
                          </span>
                        </div>
                      )}

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toast.success('Added to wishlist!');
                        }}
                        className="absolute top-3 right-3 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Heart className="w-4 h-4 text-maroon" />
                      </button>
                    </div>

                    <div className="p-4">
                      <h3 
                        className="font-heading font-semibold text-maroon mb-2 line-clamp-1 cursor-pointer hover:text-maroon-dark"
                        onClick={() => navigate(`/product/${product.id}`)}
                      >
                        {product.name}
                      </h3>

                      <div className="flex items-center gap-1 mb-2">
                        <Star className="w-4 h-4 fill-gold text-gold" />
                        <span className="text-sm font-medium text-maroon">{product.rating}</span>
                        <span className="text-sm text-maroon/60">({product.reviews_count})</span>
                      </div>

                      <div className="flex items-baseline gap-2 mb-3">
                        <span className="text-xl font-bold text-maroon">
                          ₹{product.discount_price}
                        </span>
                        {product.original_price > product.discount_price && (
                          <span className="text-sm text-maroon/50 line-through">
                            ₹{product.original_price}
                          </span>
                        )}
                      </div>

                      <Button
                        onClick={() => addToCart(product)}
                        className="w-full bg-maroon hover:bg-maroon-dark text-white font-semibold rounded-full py-2 transition-all"
                        data-testid={`add-to-cart-${product.id}`}
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Add to Cart
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Festive Banner */}
        {festiveBanner?.is_active && (
          <section 
            className="py-16 md:py-24 relative overflow-hidden"
            style={{ backgroundColor: festiveBanner.background_color || '#800000' }}
            data-testid="festive-section"
          >
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{ 
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` 
              }} />
            </div>
            
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm mb-6">
                <Flame className="w-4 h-4 text-gold" />
                <span className="text-gold font-medium">Limited Time Offer</span>
              </div>
              
              <h2 
                className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-4"
                style={{ color: festiveBanner.text_color || '#FFFFFF' }}
              >
                {festiveBanner.title}
              </h2>
              
              <p 
                className="text-lg md:text-xl mb-8 opacity-90"
                style={{ color: festiveBanner.text_color || '#FFFFFF' }}
              >
                {festiveBanner.subtitle}
              </p>
              
              <Button
                onClick={() => navigate(festiveBanner.cta_link || '/shop')}
                className="bg-gold hover:bg-gold-dark text-maroon-dark font-semibold px-8 py-6 rounded-full text-lg shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
              >
                {festiveBanner.cta_text || 'Shop Festival Collection'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </section>
        )}

        {/* Why Choose Section */}
        {whyChoose && (
          <section className="py-16 md:py-20" data-testid="why-choose-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <span className="text-gold font-accent text-sm tracking-widest">WHY US</span>
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-maroon mt-2">
                  {whyChoose.title || 'Why Choose Indicraft'}
                </h2>
                {whyChoose.subtitle && (
                  <p className="text-maroon/60 mt-3">{whyChoose.subtitle}</p>
                )}
                <div className="w-24 h-1 bg-gradient-to-r from-transparent via-gold to-transparent mx-auto mt-4" />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                {(whyChoose.items || []).map((item) => {
                  const IconComponent = iconMap[item.icon] || CheckCircle;
                  return (
                    <div 
                      key={item.id}
                      className="text-center p-6 rounded-2xl bg-white border border-gold/10 hover:border-gold/30 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4">
                        <IconComponent className="w-8 h-8 text-gold" />
                      </div>
                      <h3 className="font-heading font-semibold text-maroon mb-2">{item.title}</h3>
                      <p className="text-sm text-maroon/60">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Testimonials */}
        {testimonials.length > 0 && (
          <section className="py-16 md:py-20 bg-white" data-testid="testimonials-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <span className="text-gold font-accent text-sm tracking-widest">TESTIMONIALS</span>
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-maroon mt-2">
                  What Our Devotees Say
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-transparent via-gold to-transparent mx-auto mt-4" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.slice(0, 3).map((testimonial) => (
                  <div
                    key={testimonial.id}
                    className="bg-cream rounded-2xl p-6 md:p-8 border border-gold/10"
                    data-testid={`testimonial-${testimonial.id}`}
                  >
                    <div className="flex items-center gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-gold text-gold" />
                      ))}
                    </div>
                    
                    <Quote className="w-10 h-10 text-gold/30 mb-4" />
                    
                    <p className="text-maroon/80 leading-relaxed mb-6">
                      "{testimonial.review}"
                    </p>
                    
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-maroon/10 flex items-center justify-center">
                        <span className="text-maroon font-heading font-bold text-lg">
                          {testimonial.customer_name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-heading font-semibold text-maroon">{testimonial.customer_name}</p>
                        {testimonial.location && (
                          <p className="text-sm text-maroon/60">{testimonial.location}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Newsletter Section */}
        {newsletter && (
          <section 
            className="py-16 md:py-20"
            style={{ backgroundColor: newsletter.background_color || '#FEF3C7' }}
            data-testid="newsletter-section"
          >
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              {newsletter.discount_text && (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-maroon/10 mb-6">
                  <Sparkles className="w-4 h-4 text-maroon" />
                  <span className="text-sm font-medium text-maroon">{newsletter.discount_text}</span>
                </div>
              )}
              
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-maroon mb-4">
                {newsletter.title || 'Join Our Divine Community'}
              </h2>
              
              <p className="text-maroon/70 mb-8">
                {newsletter.subtitle || 'Subscribe for exclusive offers, new arrivals & spiritual insights'}
              </p>
              
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <Input
                  type="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder={newsletter.placeholder_text || 'Enter your email'}
                  className="h-12 rounded-full border-maroon/20 focus:border-maroon bg-white"
                  required
                />
                <Button
                  type="submit"
                  className="bg-maroon hover:bg-maroon-dark text-white font-semibold px-8 h-12 rounded-full"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {newsletter.button_text || 'Subscribe'}
                </Button>
              </form>
            </div>
          </section>
        )}
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default HomePage;
